/**
 * Title / pause / options / new-world / death screens, vanilla layout in GUI px.
 *
 *   const menus = new MenuHost(document.body, settings);
 *   menus.onPlay = (mode) => ...; menus.onNewWorld = (mode) => ...; menus.onResume = ...;
 *   menus.onQuitToTitle = ...; menus.onRespawn = ...; menus.onGameModeToggle = ...;
 *   menus.showTitle() / showPause() / showDeath(score) / hide();   menus.update(dt) per frame.
 *   menus.handleEscape(): boolean   // Esc while a menu is open: backs out of sub-screens
 *                                   // (options/new world) and returns true; false on title/hidden;
 *                                   // on the pause menu it fires onResume (true).
 *
 * Callbacks fire synchronously from the click so the lead can request pointer lock there.
 * Call `await loadKit()` before constructing. The brand is a SLOPCRAFT wordmark drawn with
 * the bitmap font (no Mojang art); the title backdrop is the vanilla panorama (CSS 3D cube)
 * when mc/gui/title/background/panorama_*.png exist, else the dirt menu background.
 */

import { GuiStage, Label, McButton, McSlider, mcText, textWidth } from './kit';
import type { GameSettings } from '../game/gameSettings';
import type { GameMode } from '../game/types';
import { CATEGORIES, CATEGORY_LABEL, getVolume, setVolume } from '../audio/volumes';

type Screen = 'none' | 'title' | 'pause' | 'options' | 'sounds' | 'newworld' | 'death';

const SPLASHES = [
  'Also try Minecraft!', 'Now in your browser!', 'Voxels!', 'Hand-crafted!', 'Made with TypeScript!',
  'Mine all the things!', 'Watch out for creepers!', 'Sssss...', '100% blocky!', 'Gravity included!',
  'Punch a tree!', 'Never dig straight down!', 'Zero chunks harmed!', 'Pixel perfect!', 'Try the bed!',
  'Now with hunger!', 'It is dark in here!', 'Craft a pickaxe!', 'Don\'t look up!', 'Open source-ish!',
];

const GRAPHICS_LABEL = { fast: 'Fast', fancy: 'Fancy', ultra: 'Ultra' } as const;
const GRAPHICS_ORDER = ['fast', 'fancy', 'ultra'] as const;

const MC = 'mc/';
const DIRT_TILE = 32; // GUI px per menu_background.png tile (vanilla draws the 16px texture at 2x)

export class MenuHost {
  readonly stage: GuiStage;
  onPlay: ((mode: GameMode | null) => void) | null = null;
  onNewWorld: ((mode: GameMode) => void) | null = null;
  onResume: (() => void) | null = null;
  onQuitToTitle: (() => void) | null = null;
  onRespawn: (() => void) | null = null;
  onGameModeToggle: (() => void) | null = null;
  /** May the pause menu offer the game-mode button? main.ts wires this to the
   *  /pin gate: without the pin there is no button at all, not a refusing one. */
  canToggleGameMode: (() => boolean) | null = null;
  /** Opens the Multiplayer screen (net/index.ts) from the pause menu; hidden while unset. */
  onMultiplayer: (() => void) | null = null;
  /**
   * The title's Play button joins the shared world (net/index.ts): onPlayWorld
   * starts the session, onPlay hands the pointer lock to the game. Kept apart
   * so Play stays inside the click gesture (pointer lock needs it).
   */
  onPlayWorld: (() => void) | null = null;
  /** Set when the build has a game server (client/entry.ts): the title then offers Singleplayer and Multiplayer. */
  onJoinServer: (() => void) | null = null;
  /** Multiplayer player name, edited in Options (net/index.ts owns the store). */
  playerName = '';
  /** Called with the new name; returns the name that was applied (may differ). */
  onPlayerName: ((name: string) => string | void) | null = null;

  private screen: Screen = 'none';
  /** Where "Done"/Cancel in options returns to. */
  private optionsFrom: 'title' | 'pause' = 'title';
  private mode: GameMode = 'survival';
  private newWorldMode: GameMode = 'survival';
  private deathScore = 0;
  private readonly splash = SPLASHES[Math.floor(Math.random() * SPLASHES.length)];
  private splashEl: HTMLDivElement | null = null;
  private pano: HTMLDivElement | null = null;
  private panoT = 0;
  private modeBtn: McButton | null = null;
  private time = 0;

  constructor(parent: HTMLElement, private readonly settings: GameSettings) {
    this.stage = new GuiStage(parent, 40);
    this.stage.setGuiScaleSetting(settings.guiScale);
    this.stage.hide();
    settings.onChange((key) => {
      if (key === 'guiScale') { this.stage.setGuiScaleSetting(settings.guiScale); this.rebuild(); }
    });
    window.addEventListener('resize', () => { if (this.screen !== 'none') this.rebuild(); });
  }

  get open(): boolean { return this.screen !== 'none'; }
  /** Which screen is up ('none' when hidden) — net/index.ts reads it to show the player list. */
  get currentScreen(): Screen { return this.screen; }

  setGameMode(m: GameMode): void {
    this.mode = m;
    this.modeBtn?.setLabel(this.modeLabel());
  }

  showTitle(): void { this.go('title'); }
  showPause(): void { this.go('pause'); }
  showDeath(score: number): void { this.deathScore = score; this.go('death'); }
  hide(): void {
    this.screen = 'none';
    this.stage.hide();
    this.stage.content.replaceChildren();
    this.pano = null;
    this.splashEl = null;
  }

  /** Esc pressed while a menu is up. */
  handleEscape(): boolean {
    switch (this.screen) {
      case 'options': this.go(this.optionsFrom); return true;
      case 'sounds': this.go('options'); return true;
      case 'newworld': this.go('title'); return true;
      case 'pause': this.onResume?.(); return true;
      default: return false;
    }
  }

  update(dt: number): void {
    this.time += dt;
    if (this.screen === 'none') return;
    if (this.pano) {
      this.panoT += dt;
      // vanilla: slow yaw (~0.1 deg per frame at 60 Hz) and a gentle pitch bob
      const yaw = -this.panoT * 6;
      const pitch = Math.sin(this.panoT * 0.4) * 5 + 10;
      this.pano.style.transform = `translateZ(0) rotateX(${pitch}deg) rotateY(${yaw}deg)`;
    }
    if (this.splashEl) {
      const ms = (this.time * 1000) % 1000;
      const f0 = 1.8 - Math.abs(Math.sin((ms / 1000) * Math.PI * 2) * 0.1);
      const f = (f0 * 100) / (textWidth(this.splash) + 32);
      this.splashEl.style.transform = `translate(${Math.floor(this.stage.width / 2) + 123}px,69px) rotate(-20deg) scale(${f})`;
    }
  }

  // ------------------------------------------------------------ plumbing ---

  private go(s: Screen): void {
    if (s === 'options' && (this.screen === 'title' || this.screen === 'pause')) this.optionsFrom = this.screen;
    this.screen = s;
    this.stage.show();
    this.rebuild();
  }

  private rebuild(): void {
    if (this.screen === 'none') return;
    this.stage.relayout();
    const c = this.stage.content;
    // keep the panorama element alive across title rebuilds (no flicker on resize)
    c.replaceChildren();
    this.splashEl = null;
    this.modeBtn = null;
    const W = this.stage.width, H = this.stage.height;
    const cx = Math.floor(W / 2);
    this.pano = null;
    switch (this.screen) {
      case 'title': this.buildTitle(c, W, H, cx); break;
      case 'pause': this.buildPause(c, W, H, cx); break;
      case 'options': this.buildOptions(c, W, H, cx); break;
      case 'sounds': this.buildSounds(c, W, H, cx); break;
      case 'newworld': this.buildNewWorld(c, W, H, cx); break;
      case 'death': this.buildDeath(c, W, H, cx); break;
    }
  }

  // ---------------------------------------------------------- primitives ---

  private tiled(path: string, x: number, y: number, w: number, h: number, size: number, shade = 1): HTMLDivElement {
    const d = document.createElement('div');
    d.style.cssText =
      `position:absolute;left:${x}px;top:${y}px;width:${w}px;height:${h}px;pointer-events:none;` +
      `background:url(${MC}${path}) 0 0/${size}px ${size}px repeat;image-rendering:pixelated;` +
      (shade < 1 ? `filter:brightness(${shade});` : '');
    return d;
  }

  private rect(x: number, y: number, w: number, h: number, bg: string): HTMLDivElement {
    const d = document.createElement('div');
    d.style.cssText = `position:absolute;left:${x}px;top:${y}px;width:${w}px;height:${h}px;background:${bg};pointer-events:none;`;
    return d;
  }

  private text(c: HTMLElement, text: string, cx: number, y: number, opts: { color?: string; shadow?: boolean; align?: 'left' | 'center' | 'right' } = {}): Label {
    const l = new Label(text, cx, y, { shadow: true, align: 'center', ...opts });
    c.appendChild(l.el);
    return l;
  }

  /** The dirt menu background (title-less screens) or the in-world dim. */
  private backdrop(c: HTMLElement, W: number, H: number, inWorld: boolean): void {
    if (inWorld) {
      // vanilla inworld background: fillGradient(0xC0101010 -> 0xD0101010)
      c.appendChild(this.rect(0, 0, W, H, 'linear-gradient(rgba(16,16,16,0.753),rgba(16,16,16,0.816))'));
    } else {
      // vanilla (1.21.2+): blurred panorama, then the translucent menu_background tile over it
      this.buildPanorama(c, W, H, 2);
      c.appendChild(this.tiled('gui/menu_background.png', 0, 0, W, H, DIRT_TILE));
    }
  }

  // --------------------------------------------------------------- title ---

  private buildTitle(c: HTMLElement, W: number, H: number, cx: number): void {
    this.buildPanorama(c, W, H);

    // wordmark: SLOPCRAFT in chunky extruded bitmap letters, vanilla-logo proportions
    const logo = this.wordmark('SLOPCRAFT');
    const lw = Math.round(logo.width / 4); // drawn at 4 px per source px => 1/4 here (logo canvas is 4x)
    logo.style.cssText =
      `position:absolute;left:${cx - Math.floor(lw / 2)}px;top:30px;width:${lw}px;height:${Math.round(logo.height / 4)}px;image-rendering:pixelated;pointer-events:none;`;
    c.appendChild(logo);
    // "Web Edition" ribbon where vanilla puts "Java Edition"
    this.text(c, 'WEB EDITION', cx - 6, 30 + 44 - 5, { color: '#dcdcdc' });

    // splash text
    const splash = document.createElement('div');
    splash.style.cssText = 'position:absolute;left:0;top:0;transform-origin:0 0;pointer-events:none;';
    const t = mcText(this.splash, { color: '#ffff00', shadow: true });
    const sw = textWidth(this.splash);
    t.style.cssText = `position:absolute;left:${-Math.floor(sw / 2)}px;top:-8px;width:${t.width}px;height:${t.height}px;image-rendering:pixelated;`;
    splash.appendChild(t);
    c.appendChild(splash);
    this.splashEl = splash;
    splash.style.transform = `translate(${cx + 123}px,69px) rotate(-20deg) scale(1.8)`;

    const y0 = Math.floor(H / 4) + 48;
    // One world, one button: Play joins the shared room (net/index.ts), and
    // the pointer lock has to be taken inside the click itself. The Multiplayer
    // screen lives in the pause menu, so the title stays Play + Options only.
    c.appendChild(new McButton(this.onJoinServer ? 'Singleplayer' : 'Play', cx - 100, y0, 200, 20, () => {
      this.onPlay?.(null);
      this.onPlayWorld?.();
    }).el);
    let y1 = y0 + 24;
    if (this.onJoinServer) { c.appendChild(new McButton('Multiplayer', cx - 100, y1, 200, 20, () => this.onJoinServer?.()).el); y1 += 24; }
    c.appendChild(new McButton('Options...', cx - 100, y1, 200, 20, () => this.go('options')).el);

    this.text(c, 'SlopCraft Web', 2, H - 10, { align: 'left' });
    const r = 'Not an official Minecraft product';
    this.text(c, r, W - 2 - textWidth(r), H - 10, { align: 'left' });
  }

  /** Vanilla panorama: 6 cube faces in a CSS 3D scene behind the GUI, slowly turning. */
  private buildPanorama(c: HTMLElement, W: number, H: number, blur = 0): void {
    const wrap = document.createElement('div');
    wrap.style.cssText = `position:absolute;left:0;top:0;width:${W}px;height:${H}px;overflow:hidden;background:#101820;pointer-events:none;${blur ? `filter:blur(${blur}px);` : ''}`;
    const scene = document.createElement('div');
    // perspective chosen for vanilla's 85deg fov: d = (H/2)/tan(42.5deg)
    const persp = Math.round(H / 2 / Math.tan((85 / 2) * Math.PI / 180));
    scene.style.cssText = `position:absolute;left:0;top:0;width:100%;height:100%;perspective:${persp}px;perspective-origin:50% 50%;`;
    const cube = document.createElement('div');
    const S = Math.max(W, H) * 2;
    cube.style.cssText = `position:absolute;left:50%;top:50%;width:0;height:0;transform-style:preserve-3d;`;
    this.pano = cube;
    const faces: Array<[number, string]> = [
      [0, `translateZ(${-S / 2}px)`],
      [1, `rotateY(-90deg) translateZ(${-S / 2}px)`],
      [2, `rotateY(180deg) translateZ(${-S / 2}px)`],
      [3, `rotateY(90deg) translateZ(${-S / 2}px)`],
      [4, `rotateX(90deg) translateZ(${-S / 2}px) rotate(180deg)`],
      [5, `rotateX(-90deg) translateZ(${-S / 2}px) rotate(180deg)`],
    ];
    for (const [i, tf] of faces) {
      const f = document.createElement('div');
      f.style.cssText =
        `position:absolute;left:${-S / 2}px;top:${-S / 2}px;width:${S}px;height:${S}px;` +
        `background:url(${MC}gui/title/background/panorama_${i}.png) 0 0/100% 100% no-repeat;` +
        `transform:${tf};backface-visibility:hidden;`;
      cube.appendChild(f);
    }
    scene.appendChild(cube);
    wrap.appendChild(scene);
    // darken like vanilla's panorama overlay so buttons/text read
    if (!blur) wrap.appendChild(this.rect(0, 0, W, H, 'linear-gradient(rgba(0,0,0,0.15),rgba(0,0,0,0.35))'));
    c.appendChild(wrap);
  }

  /**
   * "SLOPCRAFT" logo: font glyphs scaled up with a light stone face, an extruded
   * dark side and a black outline — evokes the vanilla block-letter logo without
   * using its art. Returns a canvas at 4x (callers display it at 1/4 => GUI px).
   */
  private wordmark(word: string): HTMLCanvasElement {
    const src = mcText(word, { color: '#ffffff' });
    // render at 4x of GUI px for crisp edges after the 1/4 CSS scale: canvas px = GUI px * 4
    const out = document.createElement('canvas');
    const Z = 4;
    out.width = Math.ceil(256 * Z); out.height = 44 * Z;
    const g = out.getContext('2d')!;
    g.imageSmoothingEnabled = false;
    // scale the glyph run so it spans ~ 246 GUI px (vanilla logo width 256)
    const target = 236;
    const s = target / src.width;
    const gx = (256 - target) / 2, gy = (44 - src.height * s) / 2 - 2;
    const draw = (color: string, ox: number, oy: number): void => {
      const t = document.createElement('canvas');
      t.width = out.width; t.height = out.height;
      const tg = t.getContext('2d')!;
      tg.imageSmoothingEnabled = false;
      tg.drawImage(src, (gx + ox) * Z, (gy + oy) * Z, src.width * s * Z, src.height * s * Z);
      tg.globalCompositeOperation = 'source-in';
      tg.fillStyle = color;
      tg.fillRect(0, 0, t.width, t.height);
      g.drawImage(t, 0, 0);
    };
    // black outline ring
    for (let a = 0; a < 16; a++) {
      const ang = (a / 16) * Math.PI * 2;
      draw('#000', Math.cos(ang) * 2.5, Math.sin(ang) * 2.5 + 3);
    }
    // extrusion (dark stone)
    for (let d = 3; d >= 1; d -= 0.25) draw('#3c3c3c', 0, d);
    // face
    draw('#a8a8a8', 0, 0);
    // top highlight band: lighter upper half of the face
    const hi = document.createElement('canvas');
    hi.width = out.width; hi.height = out.height;
    const hg = hi.getContext('2d')!;
    hg.imageSmoothingEnabled = false;
    hg.drawImage(src, gx * Z, gy * Z, src.width * s * Z, src.height * s * Z);
    hg.globalCompositeOperation = 'source-in';
    const grad = hg.createLinearGradient(0, gy * Z, 0, (gy + src.height * s) * Z);
    grad.addColorStop(0, '#e6e6e6');
    grad.addColorStop(0.55, '#b4b4b4');
    grad.addColorStop(1, '#7c7c7c');
    hg.fillStyle = grad;
    hg.fillRect(0, 0, hi.width, hi.height);
    g.drawImage(hi, 0, 0);
    return out;
  }

  // --------------------------------------------------------------- pause ---

  private modeLabel(): string {
    return `Game Mode: ${this.mode === 'creative' ? 'Creative' : 'Survival'}`;
  }

  private buildPause(c: HTMLElement, W: number, H: number, cx: number): void {
    this.backdrop(c, W, H, true);
    this.text(c, 'Game Menu', cx, 40, { color: '#ffffff' });
    const y = Math.floor(H / 4);
    c.appendChild(new McButton('Back to Game', cx - 102, y + 8, 204, 20, () => this.onResume?.()).el);
    let row = y + 32;
    this.modeBtn = null;
    if (this.canToggleGameMode?.() ?? true) {
      this.modeBtn = new McButton(this.modeLabel(), cx - 102, row, 204, 20, () => this.onGameModeToggle?.());
      c.appendChild(this.modeBtn.el);
      row += 24;
    }
    c.appendChild(new McButton('Options...', cx - 102, row, 204, 20, () => this.go('options')).el);
    row += 24;
    if (this.onMultiplayer) { c.appendChild(new McButton('Multiplayer', cx - 102, row, 204, 20, () => this.onMultiplayer?.()).el); row += 24; }
    c.appendChild(new McButton('Save and Quit to Title', cx - 102, row, 204, 20, () => this.onQuitToTitle?.()).el);
  }

  // ------------------------------------------------------------- options ---

  private buildOptions(c: HTMLElement, W: number, H: number, cx: number): void {
    const inWorld = this.optionsFrom === 'pause';
    this.backdrop(c, W, H, inWorld);
    const st = this.settings;
    // list area between header (33) and footer (H-33), darker like vanilla's list background
    c.appendChild(this.tiled(inWorld ? 'gui/inworld_menu_list_background.png' : 'gui/menu_list_background.png', 0, 33, W, H - 66, DIRT_TILE));
    c.appendChild(this.tiled(inWorld ? 'gui/inworld_header_separator.png' : 'gui/header_separator.png', 0, 31, W, 2, 32));
    c.appendChild(this.tiled(inWorld ? 'gui/inworld_footer_separator.png' : 'gui/footer_separator.png', 0, H - 33, W, 2, 32));
    this.text(c, 'Options', cx, 15, { color: '#ffffff' });

    const L = cx - 155, R = cx + 5, bw = 150;
    const row = (i: number): number => 37 + i * 25;

    const fovFmt = (v: number): string => {
      const f = Math.round(30 + v * 80);
      return f === 70 ? 'Normal' : f === 110 ? 'Quake Pro' : String(f);
    };
    c.appendChild(new McSlider('FOV', L, row(0), bw, (st.fov - 30) / 80, fovFmt,
      (v) => st.set('fov', Math.round(30 + v * 80))).el);

    const sensFmt = (v: number): string => (v === 0 ? '*yawn*' : v >= 1 ? 'HYPERSPEED!!!' : `${Math.round(v * 200)}%`);
    c.appendChild(new McSlider('Sensitivity', R, row(0), bw, st.sensitivity, sensFmt,
      (v) => st.set('sensitivity', v)).el);

    c.appendChild(new McSlider('Render Distance', L, row(1), bw, (st.renderDistance - 2) / 14,
      (v) => `${Math.round(2 + v * 14)} chunks`, (v) => st.set('renderDistance', Math.round(2 + v * 14))).el);

    const guiLabel = (): string => `GUI Scale: ${st.guiScale === 0 ? 'Auto' : st.guiScale}`;
    const guiBtn: McButton = new McButton(guiLabel(), R, row(1), bw, 20, () => {
      st.set('guiScale', (st.guiScale + 1) % 5); // triggers a rebuild via the settings listener
    });
    c.appendChild(guiBtn.el);

    const gfx: McButton = new McButton(`Graphics: ${GRAPHICS_LABEL[st.graphics]}`, L, row(2), bw, 20, () => {
      const n = GRAPHICS_ORDER[(GRAPHICS_ORDER.indexOf(st.graphics) + 1) % 3];
      st.set('graphics', n);
      gfx.setLabel(`Graphics: ${GRAPHICS_LABEL[n]}`);
    });
    c.appendChild(gfx.el);

    const onOff = (b: boolean): string => (b ? 'ON' : 'OFF');
    const coords: McButton = new McButton(`Show Coordinates: ${onOff(st.showCoords)}`, R, row(2), bw, 20, () => {
      st.set('showCoords', !st.showCoords);
      coords.setLabel(`Show Coordinates: ${onOff(st.showCoords)}`);
    });
    c.appendChild(coords.el);

    const bob: McButton = new McButton(`View Bobbing: ${onOff(st.viewBobbing)}`, L, row(3), bw, 20, () => {
      st.set('viewBobbing', !st.viewBobbing);
      bob.setLabel(`View Bobbing: ${onOff(st.viewBobbing)}`);
    });
    c.appendChild(bob.el);

    const aj: McButton = new McButton(`Auto-Jump: ${onOff(st.autoJump)}`, L, row(6), bw, 20, () => {
      st.set('autoJump', !st.autoJump);
      aj.setLabel(`Auto-Jump: ${onOff(st.autoJump)}`);
    });
    c.appendChild(aj.el);

    const brightFmt = (v: number): string => (v <= 0 ? 'Moody' : v >= 1 ? 'Bright' : `+${Math.round(v * 100)}%`);
    c.appendChild(new McSlider('Brightness', R, row(3), bw, st.brightness, brightFmt,
      (v) => st.set('brightness', v)).el);

    c.appendChild(new McButton('Music & Sounds...', L, row(5), bw, 20, () => this.go('sounds')).el);

    // multiplayer player name (net/index.ts stores it)
    const name = this.textInput(L, row(4), 310, this.playerName, 20, 'Name other players see');
    const commit = (): void => {
      const v = name.value.trim().slice(0, 20);
      const applied = this.onPlayerName?.(v);
      // show the name that actually took: the net layer strips what Minecraft
      // does not allow in a username, and the field must not lie about it
      name.value = typeof applied === 'string' ? applied : v;
      this.playerName = name.value;
    };
    name.addEventListener('change', commit);
    name.addEventListener('blur', commit);
    c.appendChild(name);

    c.appendChild(new McButton('Done', cx - 100, H - 27, 200, 20, () => this.go(this.optionsFrom)).el);
  }

  /** A plain text field in the GUI's dark box style (options, chat, room code). */
  private textInput(x: number, y: number, w: number, value: string, max: number, placeholder: string): HTMLInputElement {
    const el = document.createElement('input');
    el.value = value;
    el.maxLength = max;
    el.placeholder = placeholder;
    el.spellcheck = false;
    el.autocomplete = 'off';
    el.style.cssText =
      `position:absolute;left:${x}px;top:${y}px;width:${w}px;height:20px;box-sizing:border-box;` +
      'background:#000;color:#e0e0e0;border:1px solid #a0a0a0;font:8px monospace;padding:0 4px;outline:none;';
    el.addEventListener('focus', () => { el.style.borderColor = '#ffffff'; });
    el.addEventListener('blur', () => { el.style.borderColor = '#a0a0a0'; });
    // typing must never reach the game's key handlers; Esc still backs out
    for (const ev of ['keydown', 'keyup', 'keypress'] as const) {
      el.addEventListener(ev, (e) => {
        if (ev === 'keydown' && e.code === 'Escape') { this.handleEscape(); return; }
        e.stopPropagation();
      });
    }
    return el;
  }

  /** Vanilla "Music & Sounds": Master Volume full width, then the categories in two columns. */
  private buildSounds(c: HTMLElement, W: number, H: number, cx: number): void {
    const inWorld = this.optionsFrom === 'pause';
    this.backdrop(c, W, H, inWorld);
    c.appendChild(this.tiled(inWorld ? 'gui/inworld_menu_list_background.png' : 'gui/menu_list_background.png', 0, 33, W, H - 66, DIRT_TILE));
    c.appendChild(this.tiled(inWorld ? 'gui/inworld_header_separator.png' : 'gui/header_separator.png', 0, 31, W, 2, 32));
    c.appendChild(this.tiled(inWorld ? 'gui/inworld_footer_separator.png' : 'gui/footer_separator.png', 0, H - 33, W, 2, 32));
    this.text(c, 'Music & Sounds', cx, 15, { color: '#ffffff' });
    const pct = (v: number): string => (v <= 0 ? 'OFF' : `${Math.round(v * 100)}%`);
    c.appendChild(new McSlider(CATEGORY_LABEL.master, cx - 155, 37, 310, getVolume('master'), pct, (v) => setVolume('master', v)).el);
    CATEGORIES.filter((k) => k !== 'master').forEach((k, i) => {
      c.appendChild(new McSlider(CATEGORY_LABEL[k], i % 2 ? cx + 5 : cx - 155, 37 + 25 * (1 + (i >> 1)), 150, getVolume(k), pct,
        (v) => setVolume(k, v)).el);
    });
    c.appendChild(new McButton('Done', cx - 100, H - 27, 200, 20, () => this.go('options')).el);
  }

  // ----------------------------------------------------------- new world ---

  private buildNewWorld(c: HTMLElement, W: number, H: number, cx: number): void {
    this.backdrop(c, W, H, false);
    this.text(c, 'Create New World', cx, 15, { color: '#ffffff' });
    c.appendChild(this.tiled('gui/header_separator.png', 0, 31, W, 2, 32));
    c.appendChild(this.tiled('gui/footer_separator.png', 0, H - 33, W, 2, 32));

    const desc = (m: GameMode): string[] => m === 'creative'
      ? ['Unlimited resources, free flying and', 'destroy blocks instantly']
      : ['Search for resources, craft, gain', 'levels, health and hunger'];
    const modeBtn: McButton = new McButton('', cx - 100, 60, 200, 20, () => {
      this.newWorldMode = this.newWorldMode === 'survival' ? 'creative' : 'survival';
      paint();
    });
    c.appendChild(modeBtn.el);
    const l1 = new Label('', cx, 86, { color: '#a0a0a0', shadow: true, align: 'center' });
    const l2 = new Label('', cx, 96, { color: '#a0a0a0', shadow: true, align: 'center' });
    c.append(l1.el, l2.el);
    const paint = (): void => {
      modeBtn.setLabel(`Game Mode: ${this.newWorldMode === 'creative' ? 'Creative' : 'Survival'}`);
      const [a, b] = desc(this.newWorldMode);
      l1.set(a); l2.set(b);
    };
    paint();
    this.text(c, 'A new world replaces your saved world.', cx, 124, { color: '#808080' });

    c.appendChild(new McButton('Create New World', cx - 155, H - 28, 150, 20, () => this.onNewWorld?.(this.newWorldMode)).el);
    c.appendChild(new McButton('Cancel', cx + 5, H - 28, 150, 20, () => this.go('title')).el);
  }

  // --------------------------------------------------------------- death ---

  private buildDeath(c: HTMLElement, W: number, H: number, cx: number): void {
    // vanilla: fillGradient(0x60500000 -> 0xA0803030)
    c.appendChild(this.rect(0, 0, W, H, 'linear-gradient(rgba(80,0,0,0.376),rgba(128,48,48,0.627))'));
    // "You died!" at scale 2 centred on (W/2, 60)
    const title = mcText('You died!', { color: '#ffffff', shadow: true });
    const tw = textWidth('You died!');
    title.style.cssText =
      `position:absolute;left:${cx - tw}px;top:${60 - 8}px;width:${title.width * 2}px;height:${title.height * 2}px;image-rendering:pixelated;pointer-events:none;`;
    c.appendChild(title);
    // "Score: " white + number yellow, centred at y=85
    const a = 'Score: ', b = String(this.deathScore);
    const total = textWidth(a) + textWidth(b);
    const x0 = cx - Math.floor(total / 2);
    this.text(c, a, x0, 85, { align: 'left', color: '#ffffff' });
    this.text(c, b, x0 + textWidth(a), 85, { align: 'left', color: '#ffff55' });
    const y = Math.floor(H / 4);
    c.appendChild(new McButton('Respawn', cx - 100, y + 72, 200, 20, () => this.onRespawn?.()).el);
    c.appendChild(new McButton('Title Screen', cx - 100, y + 96, 200, 20, () => this.onQuitToTitle?.()).el);
  }
}
